import React, { useMemo, useRef, useState } from "react";
import styles from "./ClientNotesTab.module.css";
import i18n from "../../Services/i18n";
import { useToasts } from "../../LayoutComponents/ToastContext";
import { useModal } from "../../Context/ModalContext";
import NoteQuickForm from "../QuickFormComponents/NoteQuickForm/NoteQuickForm";
import ClientService from "../../Services/clientService";
import NoteCardItem from "../NoteCardItem/NoteCardItem";
import { usePrivileges } from "../../Context/PrivilegeContext";
import PrivilegeValidationService from "../../Services/privilegeValidationService";

const ClientNotesTab = ({ client, reloadDetailClient }) => {
  const [formKeys, setFormKeys] = useState({ formNote: 0 });
  const noteQuickFormRef = useRef(null);
  const { addToast } = useToasts();
  const { openModal } = useModal();
  const { privileges } = usePrivileges();

  // --- Nouvel état UI Sort/Filter ---
  const [sortField, setSortField] = useState("date"); // "date" | "color"
  const [sortOrder, setSortOrder] = useState("desc"); // "asc" | "desc"
  const [query, setQuery] = useState("");

  const handleAddNoteClick = async () => {
    if (
      !PrivilegeValidationService.ClientCanAddNote(await privileges(), (client.notes ?? []).length)
    ) {
      addToast(
        i18n.t("Privilege.ValidationError.MaxNoteByClient"),
        "PrivilegeValidationFailed",
        "ERROR",
        true,
      );
      return;
    }

    const currentFormKey = formKeys.formNote;
    openModal({
      title: i18n.t("NoteModal.Title"),
      hideSaveButton: true,
      form: "formHorse",
      content: <NoteQuickForm key={currentFormKey} ref={noteQuickFormRef} />,
      canClose: async () => {
        const isDirty = noteQuickFormRef.current?.isDirty?.();
        if (!isDirty) return true;

        return window.confirm(i18n.t("Modal.UnsavedChangesConfirm"));
      },
      onSave: async () => {
        try {
          const isValid = noteQuickFormRef.current.validate();
          if (!isValid) return;
          const noteData = noteQuickFormRef.current.getFormData();
          noteData.externalId = client.externalId;
          const result = await ClientService.AddNote(noteData);
          if (result) {
            reloadDetailClient(client, true);
            return true;
          } else {
            return false;
          }
        } catch (ex) {
          addToast(i18n.t("Generic.ServerError"), "BACKENDERROR", "ERROR", true);
          return false;
        }
      },
      onClose: (formName) => {
        setFormKeys((prev) => ({
          ...prev,
          [formName]: prev[formName] + 1,
        }));
      },
    });
  };

  // Helpers
  const stripHtmlToText = (html) => {
    if (!html) return "";
    const div = document.createElement("div");
    div.innerHTML = html;
    return (div.textContent || div.innerText || "").trim();
  };

  const normalizeHex = (hex) => {
    if (!hex) return "ZZZZZZ";
    return hex.toString().trim().toUpperCase();
  };

  const getTime = (d) => {
    const t = new Date(d || 0).getTime();
    return Number.isFinite(t) ? t : 0;
  };

  const processed = useMemo(() => {
    const notes = client.notes ?? [];
    if (!notes.length) return { pinned: [], others: [] };

    const q = query.trim().toLowerCase();
    const filtered = q
      ? notes.filter((n) =>
          stripHtmlToText(n?.note ?? "")
            .toLowerCase()
            .includes(q),
        )
      : notes.slice();

    // Split
    const pinned = filtered.filter((n) => n.isPinned);
    const others = filtered.filter((n) => !n.isPinned);

    // Comparateurs
    const order = sortOrder === "asc" ? 1 : -1;

    const cmpDate = (a, b) => (getTime(a.createdOn) - getTime(b.createdOn)) * order;

    const cmpColor = (a, b) =>
      normalizeHex(a.color).localeCompare(normalizeHex(b.color)) * order ||
      // tie-breaker: date desc par défaut pour rester utile
      getTime(b.createdOn) - getTime(a.createdOn);

    const sorter = sortField === "color" ? cmpColor : cmpDate;

    pinned.sort(sorter);
    others.sort(sorter);

    return { pinned, others };
  }, [client.notes, query, sortField, sortOrder]);

  const renderList = () => {
    const any = (client.notes ?? []).length > 0;
    if (!any) {
      return (
        <div className={styles.noItemFound}>
          <span>{i18n.t("Generic.List.NoItemFound")}</span>
        </div>
      );
    }

    const { pinned, others } = processed;

    return (
      <>
        {pinned.length > 0 && (
          <section className={styles.group}>
            <div className={`${styles.groupHeader} ${styles.groupHeaderPinned}`}>
              <span className={styles.groupIcon} aria-hidden>
                📌
              </span>
              <span className={styles.groupTitle}>{i18n.t("NotesTab.Pinned")}</span>
              <span className={styles.groupCount}>{pinned.length}</span>
            </div>
            <div className={styles.groupBody}>
              {pinned.map((note) => (
                <NoteCardItem
                  key={note.id || note.createdOn}
                  note={note}
                  type="client"
                  refresh={() => reloadDetailClient(client, true)}
                />
              ))}
            </div>
          </section>
        )}

        {(others.length > 0 || pinned.length === 0) && (
          <section className={styles.group}>
            {pinned.length > 0 && <div className={styles.softDivider} />}
            <div className={styles.groupHeader}>
              <span className={styles.groupTitle}>{i18n.t("NotesTab.All")}</span>
              <span className={styles.groupCount}>{others.length}</span>
            </div>
            <div className={styles.groupBody}>
              {others.map((note) => (
                <NoteCardItem
                  key={note.id || note.createdOn}
                  note={note}
                  type="client"
                  refresh={() => reloadDetailClient(client, true)}
                />
              ))}
            </div>
          </section>
        )}
      </>
    );
  };

  return (
    <div className={styles.notes}>
      <div className={styles.sectionHeader}>
        {/* Bouton Ajouter */}

        {/* Zone Sort/Filter */}
        <div className={styles.headerControls}>
          <div className={styles.controlGroup}>
            <label className={styles.controlLabel} htmlFor="sortField">
              {i18n.t("NotesTab.SortBy")}
            </label>
            <select
              id="sortField"
              className={styles.controlSelect}
              value={sortField}
              onChange={(e) => setSortField(e.target.value)}>
              <option value="date">{i18n.t("NotesTab.Date")}</option>
              <option value="color">{i18n.t("NotesTab.Color")}</option>
            </select>
          </div>

          <div className={styles.controlGroup}>
            <label className={styles.controlLabel} htmlFor="sortOrder">
              {i18n.t("NotesTab.Order")}
            </label>
            <select
              id="sortOrder"
              className={styles.controlSelect}
              value={sortOrder}
              onChange={(e) => setSortOrder(e.target.value)}>
              <option value="asc">{i18n.t("NotesTab.Asc")}</option>
              <option value="desc">{i18n.t("NotesTab.Desc")}</option>
            </select>
          </div>

          <div className={styles.controlGroupWide}>
            <input
              className={styles.searchInput}
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={i18n.t("NotesTab.FilterPlaceholder")}
              aria-label={i18n.t("NotesTab.FilterPlaceholder")}
            />
            {query && (
              <button
                type="button"
                className={styles.clearBtn}
                onClick={() => setQuery("")}
                title={i18n.t("NotesTab.Clear")}>
                ×
              </button>
            )}
          </div>
        </div>
        <button className={styles.headerButton} onClick={handleAddNoteClick}>
          {i18n.t("Generic.AddNote")}
        </button>
      </div>

      <div className={styles.sectionItemsList}>{renderList()}</div>
    </div>
  );
};

export default ClientNotesTab;
